/**
 * IMPORTS
 */
import * as commands from 'src/aggregates/customers/commands';
import * as handlers from 'src/aggregates/customers/handlers';
import selectors from 'src/aggregates/customers/selectors';
import {contactTypesChannelToId} from 'src/aggregates/customers/utils';
import {reduceContactNew} from 'src/aggregates/customers/utils';
import {reduceCustomer} from 'src/aggregates/customers/utils';
import {execute} from 'src/aggregates/execute';


/**
 * TYPES
 */
import {IContact} from 'src/aggregates/customers/state.d';
import {ICustomerAgent} from 'src/aggregates/customers/state.d';
import {ICustomerTag} from 'src/aggregates/customers/state.d';
import {IContactNew} from 'src/aggregates/customers/utils.d';


/**
 * CODE
 */

/**
 * I am an API to add a customer.
 *
 * :param agents: customer preferred agents
 * :param contacts: customer contacts
 * :param id: customer id
 * :pram name: customer name
 * :param unassociated: whether the contact is associated to a real customer
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param city: city of the customer
 * :param code: customer code
 * :param description: customer description
 * :param document: document of the customer
 * :param email: email of the customer
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param tags: customer tags
 * :param zipcode: zipcode of the customer
 *
 * :returns: promise with nothing
 */
async function add (
    agents: ICustomerAgent[],
    contacts: IContact[],
    id: number | string,
    name: string,
    unassociated: boolean,
    address1?: string,
    address2?: string,
    city?: string,
    code?: string,
    description?: string,
    document?: string,
    email?: string,
    organization?: string,
    state?: string,
    tags?: ICustomerTag[],
    zipcode?: string,
): Promise<void>
{
    await execute(
        commands.add(
            agents,
            contacts,
            id,
            name,
            unassociated,
            address1,
            address2,
            city,
            code,
            description,
            document,
            email,
            organization,
            state,
            tags,
            zipcode,
        ),
        handlers.add,
    );
}


/**
 * I am an API to set a customer search term.
 *
 * :param term: customer search term
 *
 * :returns: promise with nothing
 */
async function addSearchTerm (term: string): Promise<void>
{
    await execute(commands.addSearchTerm(term), handlers.addSearchTerm);
}


/**
 * I am an API to associate a contact to a customer.
 *
 * :param id: customer id
 * :param contact: contact uid
 * :param channel: contact's channel
 *
 * :returns: promise with nothing
 */
async function associate (id: number,
                          contact: string,
                          channel: string): Promise<void>
{
    // get the unassociated customer the contact is on
    const unassociated = selectors.getCustomerByContact(
        contactTypesChannelToId.get(channel),
        contact,
    );

    // get the contacts to be added, the clicked one and the ones held with it
    const added = unassociated?.contacts ??
        [{type_id: contactTypesChannelToId.get(channel), value: contact}];

    // reduce customer
    const reduced = reduceCustomer(selectors.fetch(id), added as IContact[]);

    // execute command, refusing contacts of another customer
    await execute(commands.update(reduced.address1,
                                  reduced.address2,
                                  reduced.agents,
                                  reduced.city,
                                  reduced.code,
                                  reduced.company,
                                  reduced.contacts,
                                  reduced.document,
                                  id,
                                  reduced.name,
                                  reduced.description,
                                  reduced.organization,
                                  reduced.state,
                                  reduced.zipcode,
                                  true),
                  handlers.update);
}


/**
 * I am an API to block a whatsapp contact.
 *
 * :param account: whatsapp account uid
 * :param contact: contact uid
 *
 * :returns: promise with nothing
 */
async function blockWhatsappContact(
    account: string,
    contact: string,
): Promise<void>
{
    await execute(commands.blockWhatsappContact(account, contact),
                  handlers.blockWhatsapp);
}


/**
 * I am an API to create a customer.
 *
 * :param address1: customer address1
 * :param address2: customer address2
 * :param city: customer city
 * :param code: customer code
 * :param company: customer company
 * :param contacts: customer contacts
 * :param countryState: customer state
 * :param name: customer name
 * :param description: customer description
 * :param organization: customer organization
 * :param zipcode: customer zipcode
 *
 * :returns: promise with nothing
 */
async function create (
    address1: string,
    address2: string,
    city: string,
    code: string,
    company: number,
    contacts: any,
    document: string,
    countryState: string,
    name: string,
    description: string,
    organization: string,
    zipcode: string,
): Promise<void>
{
    await execute(commands.create(address1,
                                  address2,
                                  city,
                                  code,
                                  company,
                                  contacts,
                                  document,
                                  name,
                                  organization,
                                  description,
                                  countryState,
                                  zipcode),
                  handlers.create);
}


/**
 * I am an API to drop the siblings of a contact that left the screen.
 *
 * :param id: contact id
 *
 * :returns: promise with nothing
 */
async function dropSiblings (id: IContact['id']): Promise<void>
{
    await execute(commands.dropSiblings(id), handlers.dropSiblings);
}


/**
 * I am an API to fetch a customer.
 *
 * :param id: customer id to be fetched
 *
 * :returns: promise with nothing
 */
async function fetch (id: number): Promise<void>
{
    await execute(commands.fetch(id), handlers.fetch);
}


/**
 * I am an API to fetch customers by tag with pagination.
 *
 * :param tag: tag id
 *
 * :returns: promise with nothing
 */
async function fetchByTagPage(tag: number): Promise<void>
{
    await execute(
        commands.fetchByTagPage(tag),
        handlers.fetchByTagPage,
    );
}


/**
 * I am an API to fetch contact types.
 *
 * :returns: promise with nothing
 */
async function fetchContactTypes (): Promise<void>
{
    await execute(commands.fetchContactTypes(), handlers.fetchContactTypes);
}


/**
 * I am an API to focus a customer.
 *
 * :param customer: customer id or path string or nothing
 *
 * :returns: promise with nothing
 */
async function focus (customer: string | number | null): Promise<void>
{
    await execute(commands.focus(customer), handlers.focus);
}


/**
 * I am an API to load customers page
 *
 * :returns: promise with nothing
 */
async function loadPage(): Promise<void>
{
    await execute(commands.loadPage(), handlers.loadPage);
}


/**
 * I am an API to load the siblings of the contact on screen.
 *
 * :param contact: contact on screen
 *
 * :returns: promise with nothing
 */
async function loadSiblings (contact: IContact): Promise<void>
{
    await execute(commands.loadSiblings(contact), handlers.loadSiblings);
}


/**
 * I am an API to modify a customer in the store.
 *
 * :param address1: address1 of the customer
 * :param address2: address2 of the customer
 * :param agents: customer preferred agents
 * :param city: city of the customer
 * :param code: customer code
 * :param contacts: customer contacts
 * :param description: customer description
 * :param document: document of the customer
 * :param id: customer id
 * :pram name: customer name
 * :param organization: organization of the customer
 * :param state: state of the customer
 * :param zipcode: zipcode of the customer
 * :param tags: customer tags
 *
 * :returns: promise with nothing
 */
async function modify(
    address1: string,
    address2: string,
    agents: ICustomerAgent[],
    city: string,
    code: string,
    contacts: IContact[],
    description: string,
    document: string,
    id: number | string,
    name: string,
    organization: string,
    state: string,
    zipcode: string,
    tags?: ICustomerTag[],
): Promise<void>
{
    await execute(commands.modify(
        address1,
        address2,
        agents,
        city,
        code,
        contacts,
        description,
        document,
        id,
        name,
        organization,
        state,
        zipcode,
        tags,
    ), handlers.modify);
}


/**
 * I am an API to update customer.
 *
 * :param id: customer id
 * :param address1: customer first address
 * :param address2: customer second address
 * :param agents: customer agents
 * :param city: customer city
 * :param code: customer code
 * :param company: customer company
 * :param contacts: customer contacts
 * :param name: customer name
 * :param description: customer description
 * :param organization: customer organization
 * :param customerState: customer state
 * :param zipcode: customer zipcode
 * :param ensureUniqueContacts: whether to refuse contacts of another customer
 *
 * :returns: promise with nothing
 */
async function update (
    id: number,
    address1: string,
    address2: string,
    agents: string[],
    city: string,
    code: string,
    company: number,
    contacts: IContactNew[],
    document: string,
    name: string,
    description: string,
    organization: string,
    customerState: string,
    zipcode: string,
    ensureUniqueContacts = false,
): Promise<void>
{
    // reduce contacts input
    const reduced = contacts.map((contact) => reduceContactNew(contact));

    // execute update
    await execute(commands.update(address1,
                                  address2,
                                  agents,
                                  city,
                                  code,
                                  company,
                                  reduced,
                                  document,
                                  id,
                                  name,
                                  description,
                                  organization,
                                  customerState,
                                  zipcode,
                                  ensureUniqueContacts),
                  handlers.update);
}


/**
 * I am an API to remove a customer.
 *
 * :param id: customer id
 *
 * :returns: promise with nothing
 */
async function remove (id: number): Promise<void>
{
    await execute(commands.remove(id), handlers.remove);
}


/**
 * I am an API to reset customer error.
 *
 * :returns: promise with nothing
 */
async function resetError (): Promise<void>
{
    await execute(commands.resetError(), handlers.resetError);
}


/**
 * I am an API to tag a customer.
 *
 * :param id: customer id
 * :param tag: tag id
 *
 * : returns: promise with nothing
 */
async function tag (id: number, tag: number): Promise<void>
{
    await execute(commands.tag(id, tag), handlers.tag);
}


/**
 * I am an API to unblock a whatsapp contact.
 *
 * :param account: whatsapp account uid
 * :param contact: contact uid
 *
 * :returns: promise with nothing
 */
async function unblockWhatsappContact(
    account: string,
    contact: string,
): Promise<void>
{
    await execute(
        commands.unblockWhatsappContact(account, contact),
        handlers.unblockWhatsapp,
    );
}


/**
 * I am an API to untag a customer.
 *
 * :param id: customer id
 * :param customerTag: customer tag association id
 *
 * :returns: promising with nothing
 */
async function untag (id: number, customerTag: number): Promise<void>
{
    await execute(commands.untag(id, customerTag), handlers.untag);
}


/**
 * I am an API to update customer tags filter.
 *
 * :returns: promise with nothing
 */
async function updateTagsFilter (tags: number[]): Promise<void>
{
    await execute(commands.updateTagsFilter(tags), handlers.updateTagsFilter);
}


/**
 * EXPORTS
 */
export {
    add,
    addSearchTerm,
    associate,
    blockWhatsappContact,
    create,
    dropSiblings,
    fetch,
    fetchByTagPage,
    fetchContactTypes,
    focus,
    loadPage,
    loadSiblings,
    modify,
    remove,
    resetError,
    tag,
    unblockWhatsappContact,
    untag,
    update,
    updateTagsFilter,
};
